// PR 1b: the calm line above the dialer — no number yet / balance empty / balance low —
// and the one-time "Your number is ready" notice. Never shown during a call. The server
// works the line out (readiness-rules.ts); it reloads on the live event readiness.changed
// (within about a minute of the change in the Call Engine) and on reconnect.

import { useCallback, useEffect, useState } from 'react';
import { closeNumberReady, getReadiness, type MyReadiness } from '../api';
import { displayNumber } from '../phone/number';

export function useReadiness(): { readiness: MyReadiness | null; reload: () => void; closeNumber: () => void } {
  const [readiness, setReadiness] = useState<MyReadiness | null>(null);
  const reload = useCallback(() => {
    getReadiness().then(setReadiness, () => undefined); // a failed read keeps what is shown
  }, []);
  useEffect(() => reload(), [reload]);
  const closeNumber = useCallback(() => {
    setReadiness((r) => (r ? { ...r, numberReady: null } : r));
    void closeNumberReady().catch(() => undefined);
  }, []);
  return { readiness, reload, closeNumber };
}

export function ReadinessBanner({ readiness, inCall, onCloseNumber }: { readiness: MyReadiness | null; inCall: boolean; onCloseNumber: () => void }) {
  if (!readiness || inCall) return null;
  const { line, numberReady } = readiness;
  return (
    <>
      {numberReady && (
        <div className="banner banner-info" role="status">
          <span>
            {numberReady.numbers.length > 1 ? 'Your phone numbers are ready: ' : 'Your phone number is ready: '}
            <strong>{numberReady.numbers.map((n) => displayNumber(n)).join(', ')}</strong>. You can make and take calls now.
          </span>
          <button className="btn btn-small btn-quiet" type="button" onClick={onCloseNumber}>
            Got it
          </button>
        </div>
      )}
      {line && (
        <div className={`banner banner-${line.tone}`} role="status" data-readiness={line.kind}>
          <span>{line.text}</span>
        </div>
      )}
    </>
  );
}
